import React, { useEffect, useState, useRef } from "react";
// libs
import { GetServerSideProps, NextPage } from "next";
import styled from "styled-components";
// components
import CustomHead from "src/components/ui/CustomHead";
import Layout from "src/layout";
import SearchContainer from "src/components/containers/search";
// hooks
import { useRouter } from "next/router";
import { useAppDispatch, useAppSelector, useModal } from "src/hooks";
import useAllPage from "src/hooks/dynamic/allPage";
// redux
import { authUserGeometrySelector, permissionSelector } from "src/redux/slices";
import { updateVariable, variablesStateKeySelector } from "src/redux/slices/variables";
// theme
import { colors } from "src/theme/colors";
// utils
import { getCenterByBounds, getZoomByQueryAreaAndLocale } from "src/utils/map";
import { setMapIsAutoFly } from "src/components/ui/Map/map";
import { fetchSearchClinics } from "src/utils/search";
import { formatSearchArea } from "src/utils/url";
// const
import { EVariants } from "src/constants/variants";
import { GEOLOCATION } from "src/constants/permissions";
import { GEOLOCATION_MODAL } from "src/constants/modals";
import {
  HIDE_GEOLOCATION_MODAL_ON_SEARCH_PAGE,
  HIDE_GEOLOCATION_MODAL_EVERYWHERE,
} from "src/constants/variables";
// types
import { TClinicDetailsSearch } from "src/types";
import { IMapProps } from "src/components/ui/Map/map.types";
import { ParsedUrlQuery } from "querystring";
import { IGeometry } from "src/constants/coordinates";
import useDeepCompareEffect from "use-deep-compare-effect";

interface ISearchPage {
  clinics: TClinicDetailsSearch[];
  count: number;
}

const Wrapper = styled.div<{ bgColor: string }>`
  background-color: ${({ bgColor }) => bgColor};
  display: flex;
  flex-direction: column;
`;

const SearchPage: NextPage<ISearchPage> = (props) => {
  const { query, asPath, locale, push } = useRouter();
  const geolocationPermission = useAppSelector(permissionSelector(GEOLOCATION));
  const hideGeolocationModalOnMount = useAppSelector(
    variablesStateKeySelector(HIDE_GEOLOCATION_MODAL_ON_SEARCH_PAGE),
  );
  const hideGeolocationModalEverywhere = useAppSelector(
    variablesStateKeySelector(HIDE_GEOLOCATION_MODAL_EVERYWHERE),
  );
  const { openModal } = useModal();
  const dispatch = useAppDispatch();
  const mapRef = useRef(null);

  const [clinics, setClinicDetails] = useState<TClinicDetailsSearch[]>(props.clinics);
  const [count, setCount] = useState<number>(props.count);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  const [mapZoom, setMapZoom] = useState<number>(getZoomByQueryAreaAndLocale(query.area, locale));

  const [asPathname, asParams] = asPath.split("?");
  const userGeometry: IGeometry = useAppSelector(authUserGeometrySelector);

  const handleFetchClinics = (query: ParsedUrlQuery, locale: string) => {
    if (query.area?.includes("service")) {
      query.services = [query.area?.[1]];
    }
    fetchSearchClinics({ pathname: asPathname, query, locale }).then(({ clinics, count }) => {
      // if (clinics?.length) {
      //   push('/no-results')
      //   return
      // }
      setClinicDetails(clinics);
      setCount(count);
    });
  };

  useEffect(() => {
    if (!query.area?.includes("search")) {
      setMapZoom(getZoomByQueryAreaAndLocale(query.area, locale));
    }
  }, [query]);

  useDeepCompareEffect(() => {
    handleFetchClinics(query, locale as string);
  }, [query]);

  useEffect(() => {
    const isModalOpened = query.open_modal === GEOLOCATION_MODAL;
    const showModal =
      !hideGeolocationModalOnMount && !hideGeolocationModalEverywhere && !isModalOpened;
    // Timer for hideGeolocationModalEverywhere
    const timer = setTimeout(() => setIsTimeout(true), 300);
    if (!isTimeout) return;

    if (
      (geolocationPermission === "prompt" || geolocationPermission === "granted") &&
      asPathname === "/veterinarian/search" &&
      showModal
    ) {
      // geolocation not asked before
      // and user search by own location
      openModal(GEOLOCATION_MODAL, {
        onSuccess: (geometry: IGeometry) => {
          if (mapRef.current) {
            setMapIsAutoFly(true);
            const duration = 1000;
            // @ts-ignore
            mapRef.current.flyTo({
              center: geometry.center,
              zoom: 13,
              duration,
            });
            // prevent many requests to BE if checkbox is active: 'Search as I move the map'
            setTimeout(() => {
              setMapIsAutoFly(false);

              // @ts-ignore
              const center = mapRef.current.getCenter();
              // @ts-ignore
              const bounds = mapRef.current.getBounds();

              const myQuery = {
                ...query,
                _start: "0",
                geo_order: `${center.lat},${center.lng}`,
                ne: [bounds.getNorthEast().lng.toFixed(4), bounds.getNorthEast().lat.toFixed(4)],
                sw: [bounds.getSouthWest().lng.toFixed(4), bounds.getSouthWest().lat.toFixed(4)],
              };

              handleFetchClinics(myQuery, locale as string);
            }, duration + 100);
          }
        },
        onCloseCb: () => {
          dispatch(updateVariable({ name: HIDE_GEOLOCATION_MODAL_ON_SEARCH_PAGE, value: true }));
        },
      });
    }
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [geolocationPermission, query, hideGeolocationModalEverywhere, isTimeout]);

  let ne: [number, number] = [userGeometry.bounds.ne.lng, userGeometry.bounds.ne.lat];
  let sw: [number, number] = [userGeometry.bounds.sw.lng, userGeometry.bounds.sw.lat];

  const setLocalBounds = () => {
    const bounds = {
      ne: {
        lng:
          typeof Number((query.ne as string[])[0]) === "number" &&
          Number((query.ne as string[])[0]),
        lat:
          typeof Number((query.ne as string[])[1]) === "number" &&
          Number((query.ne as string[])[1]),
      },
      sw: {
        lng:
          typeof Number((query.sw as string[])[0]) === "number" &&
          Number((query.sw as string[])[0]),
        lat:
          typeof Number((query.sw as string[])[1]) === "number" &&
          Number((query.sw as string[])[1]),
      },
    };

    if (bounds.ne.lng && bounds.ne.lat && bounds.sw.lng && bounds.sw.lat) {
      ne = [bounds.ne.lng, bounds.ne.lat];
      sw = [bounds.sw.lng, bounds.sw.lat];
    }
  };

  // /city OR /search with bounds params
  if (Array.isArray(query.ne) && Array.isArray(query.sw)) {
    setLocalBounds();
  } else {
    // /city OR /search without bounds params
    // if no lat, lng presented, the map will show default country location
  }

  useEffect(() => {
    // invoked when pathname toggles between '/search' and '/[city]]'
    // if (asPathname === '/veterinarian/search') return
    // updated city via place search
    if (mapRef.current) {
      // update local variables ne, sw
      let res: any;
      if (Array.isArray(query.ne) && Array.isArray(query.sw)) {
        setLocalBounds();
        res = getCenterByBounds({ ne, sw });
      } else if (clinics[0]) {
        let cityCheck;

        // This definitely isn't checking for canada....
        const canada = locale === "en-CA" || locale === "fr-CA" || locale === "en-IE";
        if (query.area && canada) {
          cityCheck = query.area?.length > 1;
        } else if (query.area) {
          cityCheck = query.area?.length > 2;
        }
        res = getCenterByBounds({ point: [clinics[0]?.data.lng, clinics[0]?.data.lat], cityCheck });
      } else {
        res = getCenterByBounds({ ne, sw });
      }
      if (res) {
        setMapIsAutoFly(true);

        const { center } = res;
        const duration = 1000;
        // @ts-ignore
        mapRef.current.flyTo({
          center,
          zoom: mapZoom,
          duration,
        });
        // prevent many requests to BE if checkbox is active: 'Search as I move the map'
        setTimeout(() => setMapIsAutoFly(false), duration + 100);
      }
    }
  }, [asPathname, mapZoom]);

  const mapProps: IMapProps = {
    containerProps: {
      ref: mapRef,
      ne,
      sw,
      clinics,
      showSearchAsMoveCheckbox: true,
    },
  };

  const getSearchArea = (): string => {
    if (asPathname === "/veterinarian/search") return "";
    const area = [...(query.area as string[])];

    return formatSearchArea(area);
  };

  const title =
    asPathname === "/veterinarian/search" ? undefined : `PetsApp | ${getSearchArea() || "Search"}`;
  const description =
    asPathname === "/veterinarian/search" ? undefined : `Search clinics in ${getSearchArea()}`;

  return (
    <>
      <CustomHead
        title={title}
        og_title={title}
        description={description}
        og_description={description}
      />

      <Wrapper bgColor={colors.white}>
        <Layout
          headerProps={{ variant: EVariants.SECONDARY, showSearchInput: true }}
          footerProps={{ styles: { maxWidth: "100%" } }}
        >
          <SearchContainer
            clinics={clinics}
            count={count}
            mapProps={mapProps}
            searchArea={getSearchArea()}
          />
        </Layout>
      </Wrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query, locale, req }) => {
  const pathname = req?.url?.split("?")[0] as string;
  const { clinics, count } = await fetchSearchClinics({
    pathname,
    query,
    locale: locale as string,
  });
  const initialReduxState = await useAllPage(locale as string);

  return {
    props: {
      initialReduxState: { DYNAMIC: initialReduxState.DYNAMIC },
      clinics,
      count,
    },
  };
};

export default SearchPage;
